import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  GitBranch, BadgeCheck, FileUp, Users, Workflow, BarChart3,
  ArrowRight, Check, Slack, Mail, Database, FileText, FileSpreadsheet,
  Image as ImageIcon, Type, Calendar, Plus, Sparkles,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { PublicHeader } from '../components/PublicHeader';
import Footer from '../components/Footer';

/* ─────────────────────────────────────────────────────────────────────────
 * Mini product "screenshot" panels — same visual language as the live app.
 * Each panel is purpose-built per feature so the hero of every section
 * actually shows the capability, not a generic stock image.
 * ─────────────────────────────────────────────────────────────────────── */

const PreviewConditionalLogic = () => {
  return (
    <div className="p-5 space-y-3 text-[11px]">
      <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-white border border-slate-200">
        <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 font-mono font-bold">IF</span>
        <span className="text-slate-700">Country</span>
        <span className="text-slate-400">=</span>
        <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-bold">United States</span>
      </div>
      <div className="ml-3 pl-3 border-l-2 border-indigo-200 space-y-2">
        <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-emerald-50/60">
          <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-mono font-bold">THEN</span>
          <span className="text-slate-700 font-medium">Show field</span>
          <span className="px-1.5 py-0.5 rounded bg-white border border-emerald-200 text-slate-700">State</span>
        </div>
        <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-amber-50/60">
          <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-mono font-bold">ELSE</span>
          <span className="text-slate-700 font-medium">Hide field</span>
          <span className="px-1.5 py-0.5 rounded bg-white border border-amber-200 text-slate-700">State</span>
        </div>
        <motion.div
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-rose-50/60"
        >
          <span className="px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 font-mono font-bold">ROUTE</span>
          <span className="text-slate-700 font-medium">Send to</span>
          <span className="px-1.5 py-0.5 rounded bg-white border border-rose-200 text-slate-700">Sales team</span>
        </motion.div>
      </div>
    </div>
  );
};

const PreviewApprovals = () => {
  const steps = [
    { label: 'Submitted',        sub: 'Sarah Patel',     state: 'done' },
    { label: 'Manager Review',   sub: 'Approved by Alex', state: 'done' },
    { label: 'Finance',          sub: 'In review...',     state: 'active' },
    { label: 'Final Sign-off',   sub: 'Pending',          state: 'pending' },
  ];
  return (
    <div className="p-5 space-y-2 text-[11px]">
      {steps.map((s, i) => (
        <motion.div
          key={s.label}
          initial={{ opacity: 0, x: -8 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: i * 0.1 }}
          className={`flex items-center gap-3 px-3 py-2 rounded-lg ${
            s.state === 'active' ? 'bg-indigo-50/80 ring-1 ring-indigo-100' : 'bg-white border border-slate-100'
          }`}
        >
          <div className={`w-5 h-5 rounded-full flex items-center justify-center ring-2 ${
            s.state === 'done'   ? 'bg-emerald-500 ring-emerald-100' :
            s.state === 'active' ? 'bg-indigo-500 ring-indigo-100 animate-pulse' :
                                   'bg-slate-200 ring-slate-100'
          }`}>
            {s.state === 'done' && <Check className="w-3 h-3 text-white" strokeWidth={3.5} />}
          </div>
          <div className="flex-1 min-w-0">
            <p className={`font-semibold ${s.state === 'pending' ? 'text-slate-400' : 'text-slate-800'}`}>{s.label}</p>
            <p className="text-slate-500 text-[10px]">{s.sub}</p>
          </div>
          <span className={`text-[9px] font-bold uppercase tracking-wider ${
            s.state === 'done'   ? 'text-emerald-600' :
            s.state === 'active' ? 'text-indigo-600' :
                                   'text-slate-400'
          }`}>
            {s.state}
          </span>
        </motion.div>
      ))}
    </div>
  );
};

const PreviewFileUploads = () => {
  const files = [
    { Icon: FileText,        name: 'NDA-signed.pdf',    size: '184 KB', color: 'text-rose-500    bg-rose-50',    progress: 100 },
    { Icon: ImageIcon,       name: 'site-photo.jpg',    size: '2.1 MB', color: 'text-violet-500  bg-violet-50',  progress: 100 },
    { Icon: FileSpreadsheet, name: 'budget-2026.xlsx',  size: '47 KB',  color: 'text-emerald-500 bg-emerald-50', progress: 72  },
  ];
  return (
    <div className="p-5 space-y-2.5 text-[11px]">
      {files.map((f, i) => {
        const Icon = f.Icon;
        return (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 6 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            className="flex items-center gap-3 px-3 py-2 bg-white rounded-lg border border-slate-100"
          >
            <span className={`w-9 h-9 rounded-lg ${f.color} flex items-center justify-center shrink-0`}>
              <Icon className="w-4 h-4" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-0.5">
                <span className="font-semibold text-slate-800 truncate">{f.name}</span>
                <span className="text-slate-400 shrink-0">{f.size}</span>
              </div>
              <div className="h-1 rounded-full bg-slate-100 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: `${f.progress}%` }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.2 + i * 0.1 }}
                  className={`h-full ${f.progress === 100 ? 'bg-emerald-500' : 'bg-indigo-500'}`}
                />
              </div>
            </div>
            {f.progress === 100 ? (
              <Check className="w-4 h-4 text-emerald-500 shrink-0" strokeWidth={3} />
            ) : (
              <span className="text-[10px] font-bold text-indigo-600">{f.progress}%</span>
            )}
          </motion.div>
        );
      })}
    </div>
  );
};

const PreviewTeam = () => {
  const members = [
    { initials: 'AC', name: 'Alex Chen',     role: 'Owner',   color: 'bg-indigo-500'  },
    { initials: 'MR', name: 'Maya Rao',      role: 'Editor',  color: 'bg-emerald-500' },
    { initials: 'JK', name: 'Jordan Kim',    role: 'Viewer',  color: 'bg-amber-500'   },
    { initials: 'TP', name: 'Taylor Park',   role: 'Editor',  color: 'bg-rose-500'    },
  ];
  return (
    <div className="p-5 space-y-2.5 text-[11px]">
      <div className="flex items-center justify-between mb-1">
        <span className="font-semibold text-slate-700">Team members</span>
        <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 ring-1 ring-emerald-100 px-2 py-0.5 rounded-full text-[10px] font-bold">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          3 online
        </span>
      </div>
      {members.map((m, i) => (
        <motion.div
          key={m.initials}
          initial={{ opacity: 0, x: -6 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: i * 0.08 }}
          className="flex items-center gap-3 px-2.5 py-1.5 rounded-lg bg-white border border-slate-100"
        >
          <div className={`w-8 h-8 rounded-full ${m.color} text-white font-bold text-[10px] flex items-center justify-center shrink-0 ring-2 ring-white shadow-sm`}>
            {m.initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-slate-800 truncate">{m.name}</p>
            <p className="text-slate-500 text-[10px]">{m.role}</p>
          </div>
          {i < 3 && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}
        </motion.div>
      ))}
    </div>
  );
};

const PreviewAutomation = () => {
  const steps = [
    { Icon: FileText, label: 'Form submitted', color: 'bg-indigo-500'  },
    { Icon: Slack,    label: 'Slack notify',   color: 'bg-emerald-500' },
    { Icon: Database, label: 'CRM update',     color: 'bg-violet-500'  },
    { Icon: Mail,     label: 'Auto-reply',     color: 'bg-rose-500'    },
  ];
  return (
    <div className="p-5 space-y-2 text-[11px]">
      {steps.map((s, i) => {
        const Icon = s.Icon;
        return (
          <React.Fragment key={s.label}>
            <motion.div
              initial={{ opacity: 0, x: -6 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.12 }}
              className="flex items-center gap-3 px-3 py-2 bg-white rounded-lg border border-slate-100"
            >
              <span className={`w-8 h-8 rounded-lg ${s.color} text-white flex items-center justify-center shadow-sm shrink-0`}>
                <Icon className="w-4 h-4" />
              </span>
              <span className="font-semibold text-slate-800 flex-1">{s.label}</span>
              <motion.span
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 + i * 0.12 }}
                className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-[9px] font-bold"
              >
                ✓
              </motion.span>
            </motion.div>
            {i < steps.length - 1 && (
              <motion.div
                initial={{ scaleY: 0 }}
                whileInView={{ scaleY: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 + i * 0.12 }}
                style={{ originY: 0 }}
                className="ml-6 w-px h-2 bg-slate-200"
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const PreviewAnalytics = () => {
  const bars = [40, 55, 65, 80, 50, 92, 78];
  return (
    <div className="p-5 space-y-3 text-[11px]">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-bold text-slate-800 text-[13px]">1,284</p>
          <p className="text-slate-500">Submissions · last 7 days</p>
        </div>
        <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 ring-1 ring-emerald-100 px-2 py-0.5 rounded-full font-bold">
          ↑ 24.5%
        </span>
      </div>
      <div className="flex items-end gap-1.5 h-20">
        {bars.map((h, i) => (
          <motion.div
            key={i}
            initial={{ height: 0 }}
            whileInView={{ height: `${h}%` }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.05 }}
            className={`flex-1 rounded-t ${i === 5 ? 'bg-indigo-500' : 'bg-indigo-200'}`}
          />
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-100">
        <div><p className="text-slate-500">Views</p><p className="font-bold text-slate-800">4,910</p></div>
        <div><p className="text-slate-500">Conv. rate</p><p className="font-bold text-slate-800">26.1%</p></div>
        <div><p className="text-slate-500">Avg. time</p><p className="font-bold text-slate-800">1:42</p></div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
 * Feature blocks config — every entry answers "why does this matter?"
 * ─────────────────────────────────────────────────────────────────────── */

const FEATURE_BLOCKS = [
  {
    id: 'conditional-logic',
    eyebrow: 'Conditional Logic',
    headline: 'Forms that adapt to every answer.',
    why:
      'Long, generic forms scare people off. With Formlio, fields appear, disappear, and route in real time — so every person fills the shortest form possible. Your completion rate jumps, your follow-ups get sharper, and you stop bothering customers with questions that don\'t apply to them.',
    bullets: [
      'Show/hide fields based on previous answers',
      'Route submissions to the right team automatically',
      'Pre-fill known values to cut typing in half',
    ],
    cta: { label: 'Try the form builder', to: '/signup' },
    accent: 'from-indigo-500 to-violet-500',
    badge: 'bg-indigo-50 text-indigo-700 ring-indigo-100',
    Preview: PreviewConditionalLogic,
    video: '/videos/form-builder-demo.mp4',
  },
  {
    id: 'approvals',
    eyebrow: 'Approval Workflows',
    headline: 'Sign-offs that happen in hours, not weeks.',
    why:
      'Manual approval chains cost real revenue — purchases stall, leave requests rot, expense reports get forgotten. Formlio routes every submission through the exact approval chain you need, with reminders, escalations, and a full audit trail. Stakeholders sign-off in one tap; nothing slips through the cracks.',
    bullets: [
      'Multi-step chains with parallel or sequential approvers',
      'Auto-reminders + escalation when someone goes idle',
      'Tamper-proof audit log for every decision',
    ],
    cta: { label: 'See approval flows', to: '/signup' },
    accent: 'from-amber-500 to-orange-500',
    badge: 'bg-amber-50 text-amber-700 ring-amber-100',
    Preview: PreviewApprovals,
  },
  {
    id: 'file-uploads',
    eyebrow: 'File Uploads',
    headline: 'Collect any document — without the email back-and-forth.',
    why:
      'Chasing customers for signed PDFs, ID copies, or proof of address kills your speed. Formlio lets respondents upload anything you need on the form itself, auto-organizes the files into your Google Drive or cloud storage, and ties every file to the exact submission it came from. No more "can you re-send that attachment?" emails.',
    bullets: [
      'Up to 100 MB per file, any type you allow',
      'Auto-sync to Google Drive, Dropbox, or S3',
      'Files attached to the submission audit trail forever',
    ],
    cta: { label: 'Build an intake form', to: '/signup' },
    accent: 'from-violet-500 to-fuchsia-500',
    badge: 'bg-violet-50 text-violet-700 ring-violet-100',
    Preview: PreviewFileUploads,
  },
  {
    id: 'team',
    eyebrow: 'Team Collaboration',
    headline: 'One workspace your whole team actually uses.',
    why:
      'When forms live on one person\'s account, the bus factor is one — vacations and turnover become disasters. Formlio lets you invite teammates with role-based access (Owner, Editor, Viewer), share form drafts, and triage submissions together from a shared inbox. Everyone sees the same source of truth.',
    bullets: [
      'Role-based access: Owner, Editor, Viewer',
      'Shared submissions inbox with assign + comment',
      'Activity log shows who changed what, when',
    ],
    cta: { label: 'Invite your team', to: '/signup' },
    accent: 'from-blue-500 to-sky-500',
    badge: 'bg-blue-50 text-blue-700 ring-blue-100',
    Preview: PreviewTeam,
  },
  {
    id: 'automation',
    eyebrow: 'Workflow Automation',
    headline: 'Turn every submission into an instant business action.',
    why:
      'A form that only collects data is a half-built solution. Formlio fires the next step automatically — Slack pings, CRM rows, calendar invites, customer emails — the moment someone hits Submit. Your team stops doing copy-paste work and starts doing real work.',
    bullets: [
      '30+ native integrations + custom webhooks',
      'Branching triggers based on form answers',
      'Retry-safe delivery with full delivery logs',
    ],
    cta: { label: 'Explore integrations', to: '/integrations' },
    accent: 'from-emerald-500 to-teal-500',
    badge: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
    Preview: PreviewAutomation,
    video: '/videos/integrations-demo.mp4',
  },
  {
    id: 'analytics',
    eyebrow: 'Analytics',
    headline: 'Know exactly where your forms are leaking money.',
    why:
      'Most teams have no idea why their forms convert at 12% instead of 40%. Formlio tracks every view, drop-off point, and time-spent metric — so you know which fields scare people away and which CTAs convert. Stop guessing what to A/B test; the data shows you.',
    bullets: [
      'View → start → submit conversion funnel',
      'Per-field drop-off heatmap',
      'Trend lines, daily/weekly/monthly comparisons',
    ],
    cta: { label: 'See live analytics', to: '/signup' },
    accent: 'from-cyan-500 to-blue-500',
    badge: 'bg-cyan-50 text-cyan-700 ring-cyan-100',
    Preview: PreviewAnalytics,
  },
];

/* ─────────────────────────────────────────────────────────────────────────
 * Page component
 * ─────────────────────────────────────────────────────────────────────── */

const FeatureBlock = ({ block, index }) => {
  const { eyebrow, headline, why, bullets, cta, accent, badge, Preview, video } = block;
  const flip = index % 2 === 1; // alternate sides

  return (
    <section
      className="py-16 lg:py-24"
      data-testid={`feature-block-${block.id}`}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${
          flip ? 'lg:[&>*:first-child]:order-2' : ''
        }`}>
          {/* Left/text column */}
          <motion.div
            initial={{ opacity: 0, x: flip ? 30 : -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.5 }}
          >
            <span className={`inline-flex items-center px-3 py-1 rounded-full ring-1 text-[11px] font-bold tracking-widest uppercase mb-4 ${badge}`}>
              {eyebrow}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-[2.6rem] font-bold text-slate-900 leading-[1.1] tracking-tight mb-5">
              {headline}
            </h2>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-6">
              {why}
            </p>
            <ul className="space-y-2.5 mb-8">
              {bullets.map((b, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, y: 6 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 + i * 0.08 }}
                  className="flex items-start gap-3 text-sm sm:text-base text-slate-700"
                >
                  <span className={`shrink-0 mt-0.5 w-5 h-5 rounded-full bg-gradient-to-br ${accent} text-white flex items-center justify-center`}>
                    <Check className="w-3 h-3" strokeWidth={3} />
                  </span>
                  <span>{b}</span>
                </motion.li>
              ))}
            </ul>
            <Button
              asChild
              size="lg"
              className={`rounded-full h-12 px-7 shadow-lg text-white bg-gradient-to-r ${accent} hover:shadow-xl hover:scale-[1.02] transition-all`}
              data-testid={`feature-cta-${block.id}`}
            >
              <Link to={cta.to} className="inline-flex items-center gap-2">
                {cta.label}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          </motion.div>

          {/* Right/screenshot column */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.5, delay: 0.15 }}
            whileHover={{ y: -6 }}
            className="relative"
          >
            {/* Soft tinted halo */}
            <div
              aria-hidden
              className={`absolute -inset-6 bg-gradient-to-tr ${accent} opacity-10 blur-2xl rounded-3xl -z-10`}
            />

            {/* Browser chrome wrapper */}
            <div className="rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/10 overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50/80">
                <div className="flex gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                  <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
                </div>
                <div className="mx-auto bg-white border border-slate-200 rounded-md px-3 py-0.5 text-[10px] text-slate-500 font-mono flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  formlio.app
                </div>
              </div>
              <div className="bg-gradient-to-br from-slate-50 via-white to-slate-50">
                {video ? (
                  <video
                    src={video}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    onCanPlay={(e) => e.target.play()}
                    className="block w-full aspect-video object-cover bg-slate-100"
                    data-testid={`feature-video-${block.id}`}
                  />
                ) : (
                  <Preview />
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const FeaturesPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="features-page">
      <PublicHeader />

      {/* ─── Hero ─────────────────────────────────────────────────────── */}
      <section
        className="relative pt-16 lg:pt-24 pb-12 lg:pb-16 overflow-hidden bg-gradient-to-b from-indigo-50/40 via-white to-white"
        data-testid="features-hero"
      >
        {/* Soft decorative blobs */}
        <div aria-hidden className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-indigo-100/60 blur-3xl pointer-events-none" />
        <div aria-hidden className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-violet-100/60 blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 relative text-center">
          <motion.span
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border border-indigo-100 text-[11px] font-bold tracking-widest uppercase text-indigo-600 mb-5 shadow-sm"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Features
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-[1.05] mb-6 max-w-4xl mx-auto"
            data-testid="features-hero-heading"
          >
            Everything you need to{' '}
            <span className="bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">
              automate data collection
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="text-base sm:text-lg lg:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto mb-8"
          >
            Build intelligent workflows with forms, approvals, automations, and integrations — the platform your team actually wants to use.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-3"
          >
            <Button
              asChild
              size="lg"
              className="rounded-full h-12 px-7 shadow-lg bg-indigo-600 hover:bg-[#F59E0B] text-white shadow-indigo-500/25 transition-all duration-300"
              data-testid="features-hero-cta"
            >
              <Link to="/signup" className="inline-flex items-center gap-2">
                Start Free
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="rounded-full h-12 px-7 border-slate-300 hover:border-indigo-300 hover:bg-indigo-50 transition-all duration-300"
              data-testid="features-hero-templates"
            >
              <Link to="/templates">Explore Templates</Link>
            </Button>
          </motion.div>

          {/* Feature quick-jump pills */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.4 }}
            className="mt-10 flex flex-wrap items-center justify-center gap-2"
          >
            {[
              { id: 'conditional-logic', Icon: GitBranch,  label: 'Conditional Logic' },
              { id: 'approvals',         Icon: BadgeCheck, label: 'Approvals' },
              { id: 'file-uploads',      Icon: FileUp,     label: 'File Uploads' },
              { id: 'team',              Icon: Users,      label: 'Team' },
              { id: 'automation',        Icon: Workflow,   label: 'Automation' },
              { id: 'analytics',         Icon: BarChart3,  label: 'Analytics' },
            ].map(({ id, Icon, label }) => (
              <a
                key={id}
                href={`#${id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-medium text-slate-700 hover:border-indigo-300 hover:text-indigo-700 hover:shadow-sm transition-all"
                data-testid={`features-jump-${id}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </a>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ─── Feature Blocks ──────────────────────────────────────────── */}
      <div className="divide-y divide-slate-100">
        {FEATURE_BLOCKS.map((block, i) => (
          <div key={block.id} id={block.id} className={i % 2 === 1 ? 'bg-slate-50/60' : 'bg-white'}>
            <FeatureBlock block={block} index={i} />
          </div>
        ))}
      </div>

      {/* ─── Final CTA ───────────────────────────────────────────────── */}
      <section
        className="relative py-20 lg:py-24 bg-gradient-to-br from-indigo-600 to-violet-600 overflow-hidden"
        data-testid="features-final-cta"
      >
        <div aria-hidden className="absolute inset-0 opacity-20" style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }} />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto px-6 text-center relative"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-5">
            Stop building. Start{' '}
            <span className="bg-gradient-to-r from-amber-300 to-orange-200 bg-clip-text text-transparent">
              shipping outcomes
            </span>
            .
          </h2>
          <p className="text-base sm:text-lg text-indigo-100 mb-8 max-w-xl mx-auto">
            Try every feature free for 14 days. No credit card. No engineers required.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button
              asChild
              size="lg"
              className="rounded-full h-14 px-8 text-base font-semibold bg-white hover:bg-[#F59E0B] text-indigo-700 hover:text-white shadow-2xl shadow-black/20 transition-all"
              data-testid="features-final-cta-start"
            >
              <Link to="/signup" className="inline-flex items-center gap-2">
                Start Free
                <ArrowRight className="w-5 h-5" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="rounded-full h-14 px-8 text-base font-semibold bg-transparent border-2 border-white/40 text-white hover:bg-white/10 hover:border-white transition-all"
              data-testid="features-final-cta-demo"
            >
              <Link to="/contact">Schedule Demo</Link>
            </Button>
          </div>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
};

export default FeaturesPage;
export { FeaturesPage };
